<template>
  <div class="show-map-container h-full relative">
    <div id="show-map" class="w-full h-full bg-gray-200"></div>
  </div>
</template>

<script>
import MapInfoWindow from '@/components/MapInfoWindow.vue'
import Vue from 'vue'

/**
 * ShowMap Component
 * 
 * A fully reusable map component that displays show markers for the provided shows array.
 * All shows passed to this component will be displayed on the map.
 * 
 * Props:
 * - shows: Array of show objects to display as markers on the map
 *   Each show should have: location_details.geolocation.{latitude, longitude}
 * 
 * Features:
 * - Google Maps integration with custom zoom controls
 * - Interactive markers with detailed info windows
 * - Location-based centering and smart zoom levels
 * - Radius filtering visualization (opacity-based, does not filter shows)
 * - Map bounds tracking for parent components (parent handles bounds-based filtering)
 * - Responsive design with mobile support
 * 
 * Behavior:
 * - Shows all markers passed via props (no filtering at component level)
 * - Radius filtering only affects visual appearance (opacity)
 * - Shows within radius: full opacity (1.0)
 * - Shows outside radius: reduced opacity (0.3)
 * - Parent component controls which shows to display based on bounds/filters
 * 
 * Usage Examples:
 * 
 * // Display all shows (no filtering)
 * <ShowMap :shows="allShows" />
 * 
 * // Display map-bounds filtered shows (with radius visual feedback)
 * <ShowMap :shows="showsInMapBounds" />
 * 
 * // Display search-filtered shows (with radius visual feedback)
 * <ShowMap :shows="filteredShows" />
 * 
 * The component automatically handles:
 * - Marker creation/removal when shows prop changes
 * - Map centering based on search location
 * - Radius visualization (circle + opacity effects)
 * - Info window content with show details
 * - Map bounds tracking for parent filtering decisions
 */

export default {
  name: 'ShowMap',
  components: {
    MapInfoWindow
  },
  props: {
    shows: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      centerMarker: null,
      userInteracting: false,
      initialLoad: true,
      openingInfoWindow: false,
      zoomControl: null, // Add reference to custom zoom control
      boundaryControl: null, // Add reference to boundary control
      // Add radius filtering visual elements
      radiusCircle: null,
      filteredMarkers: [],
      clearingFromBoundaryControl: false, // Flag to prevent map repositioning when clearing from boundary control
      stateBounds: {
        'AL': { north: 35.008, south: 30.223, east: -84.889, west: -88.473 },
        'AK': { north: 71.538, south: 51.209, east: -129.992, west: -179.148 },
        'AZ': { north: 37.004, south: 31.332, east: -109.045, west: -114.816 },
        'AR': { north: 36.499, south: 33.004, east: -89.645, west: -94.617 },
        'CA': { north: 42.009, south: 32.534, east: -114.131, west: -124.409 },
        'CO': { north: 41.003, south: 36.993, east: -102.041, west: -109.060 },
        'CT': { north: 42.050, south: 40.980, east: -71.787, west: -73.727 },
        'DE': { north: 39.839, south: 38.451, east: -75.049, west: -75.789 },
        'FL': { north: 31.001, south: 24.396, east: -79.974, west: -87.635 },
        'GA': { north: 35.000, south: 30.356, east: -80.751, west: -85.605 },
        'HI': { north: 28.517, south: 18.911, east: -154.806, west: -178.334 },
        'ID': { north: 49.001, south: 41.988, east: -111.043, west: -117.243 },
        'IL': { north: 42.508, south: 36.970, east: -87.494, west: -91.513 },
        'IN': { north: 41.761, south: 37.772, east: -84.784, west: -88.098 },
        'IA': { north: 43.501, south: 40.376, east: -90.140, west: -96.640 },
        'KS': { north: 40.003, south: 36.993, east: -94.589, west: -102.051 },
        'KY': { north: 39.147, south: 36.497, east: -81.965, west: -89.571 },
        'LA': { north: 33.019, south: 28.928, east: -88.817, west: -94.043 },
        'ME': { north: 47.460, south: 43.098, east: -66.885, west: -71.084 },
        'MD': { north: 39.723, south: 37.886, east: -75.049, west: -79.487 },
        'MA': { north: 42.887, south: 41.187, east: -69.858, west: -73.508 },
        'MI': { north: 48.306, south: 41.696, east: -82.122, west: -90.418 },
        'MN': { north: 49.384, south: 43.499, east: -89.491, west: -97.239 },
        'MS': { north: 35.008, south: 30.146, east: -88.097, west: -91.655 },
        'MO': { north: 40.613, south: 35.995, east: -89.098, west: -95.774 },
        'MT': { north: 49.001, south: 44.358, east: -104.039, west: -116.063 },
        'NE': { north: 43.002, south: 39.999, east: -95.308, west: -104.053 },
        'NV': { north: 42.002, south: 35.002, east: -114.040, west: -120.006 },
        'NH': { north: 45.306, south: 42.697, east: -70.610, west: -72.557 },
        'NJ': { north: 41.357, south: 38.788, east: -73.894, west: -75.563 },
        'NM': { north: 37.000, south: 31.332, east: -103.002, west: -109.050 },
        'NY': { north: 45.015, south: 40.477, east: -71.777, west: -79.762 },
        'NC': { north: 36.588, south: 33.752, east: -75.460, west: -84.322 },
        'ND': { north: 49.000, south: 45.935, east: -96.554, west: -104.049 },
        'OH': { north: 41.977, south: 38.403, east: -80.519, west: -84.820 },
        'OK': { north: 37.002, south: 33.615, east: -94.431, west: -103.002 },
        'OR': { north: 46.292, south: 41.992, east: -116.463, west: -124.703 },
        'PA': { north: 42.516, south: 39.720, east: -74.689, west: -80.519 },
        'RI': { north: 42.019, south: 41.146, east: -71.120, west: -71.862 },
        'SC': { north: 35.216, south: 32.033, east: -78.541, west: -83.353 },
        'SD': { north: 45.945, south: 42.479, east: -96.436, west: -104.058 },
        'TN': { north: 36.678, south: 34.983, east: -81.647, west: -90.310 },
        'TX': { north: 36.501, south: 25.837, east: -93.508, west: -106.645 },
        'UT': { north: 42.001, south: 36.998, east: -109.041, west: -114.053 },
        'VT': { north: 45.017, south: 42.727, east: -71.465, west: -73.354 },
        'VA': { north: 39.466, south: 36.541, east: -75.166, west: -83.675 },
        'WA': { north: 49.002, south: 45.544, east: -116.916, west: -124.848 },
        'WV': { north: 40.638, south: 37.202, east: -77.719, west: -82.644 },
        'WI': { north: 47.081, south: 42.492, east: -86.249, west: -92.889 },
        'WY': { north: 45.006, south: 40.994, east: -104.052, west: -111.056 }
      }
    }
  },
  computed: {
    selectedLocation() {
      return this.$store.state.searchParams.location
    },
    selectedRadius() {
      return this.$store.state.searchParams.radius
    },
    distanceType() {
      return this.$store.state.searchParams.distanceType
    }
  },
  mounted() {
    this.initializeMap()
  },
  watch: {
    shows: {
      handler(newShows, oldShows) {
        if (this.map) {
          // Always update markers when shows prop changes
          this.updateMarkers(newShows)
          // Update radius filtering if location is selected
          if (this.selectedLocation && this.selectedRadius) {
            this.updateRadiusFiltering()
          }
        }
      },
      deep: true
    },
    '$store.state.searchParams.location': {
      handler(newLocation, oldLocation) {
        if (this.map) {
          if (newLocation) {
            this.centerMapOnLocation(newLocation)
            // Update radius filtering when location changes
            if (this.selectedRadius) {
              this.updateRadiusFiltering()
            }
          } else if (oldLocation && !newLocation) {
            // Only reposition map if not clearing from boundary control
            if (!this.clearingFromBoundaryControl) {
              // Location was cleared, center map on US and clear radius filtering
              this.centerMapOnUS()
            }
            this.clearRadiusFiltering()
          }
        }
      },
      deep: true
    },
    '$store.state.searchParams.radius': {
      handler(newRadius, oldRadius) {
        if (this.map && this.selectedLocation) {
          if (newRadius) {
            this.updateRadiusFiltering()
          } else if (oldRadius && !newRadius) {
            this.clearRadiusFiltering()
          }
        }
      }
    },
    '$store.state.searchParams.distanceType': {
      handler(newType, oldType) {
        if (this.map && this.selectedLocation && this.selectedRadius) {
          this.updateRadiusFiltering()
        }
      }
    }
  },
  methods: {
    async initializeMap() {
      try {
        // Load Google Maps API if not already loaded
        if (!window.google || !window.google.maps) {
          await this.loadGoogleMapsAPI()
        }
        
        // Double-check that Google Maps is available
        if (!window.google || !window.google.maps) {
          throw new Error('Google Maps API not available after loading')
        }
        
        // Get current search parameters
        const searchParams = this.$store.state.searchParams
        
        // Create map
        this.map = new window.google.maps.Map(document.getElementById('show-map'), {
          zoom: 5,
          center: { lat: 39.8283, lng: -98.5795 }, // Center of US
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          // Disable default zoom controls since we're adding custom ones
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true,
          disableDefaultUI: true,
          gestureHandling: 'greedy'
        })
        
        // Add custom zoom controls to the map
        this.zoomControl = this.createZoomControl()
        this.map.controls[window.google.maps.ControlPosition.RIGHT_TOP].push(this.zoomControl)
        
        // Add boundary control to the map
        this.boundaryControl = this.createBoundaryControl()
        this.map.controls[window.google.maps.ControlPosition.TOP_CENTER].push(this.boundaryControl)
        
        // Initialize map based on search parameters
        if (searchParams.location) {
          this.centerMapOnLocation(searchParams.location)
          
          // Apply radius filtering if radius is set
          if (searchParams.radius) {
            this.updateRadiusFiltering()
          }
        } else {
          this.centerMapOnUS()
        }
        
        // Add map bounds change listener
        this.map.addListener('bounds_changed', () => {
          if (!this.userInteracting && !this.initialLoad) {
            this.updateMapBounds()
          }
        })
        
        // Add user interaction listeners
        this.map.addListener('dragstart', () => {
          this.userInteracting = true
        })
        
        this.map.addListener('dragend', () => {
          this.userInteracting = false
          this.updateMapBounds()
        })
        
        this.map.addListener('zoom_changed', () => {
          if (!this.userInteracting && !this.initialLoad) {
            this.updateMapBounds()
          }
        })
        
        // Update markers with current shows
        this.updateMarkers(this.shows)
        
        // Initial bounds update
        this.updateMapBounds()
        
        // Mark initial load as complete
        this.initialLoad = false
        
      } catch (error) {
        console.error('Error initializing map:', error)
        // Fallback: show error message in map container
        const mapContainer = document.getElementById('show-map')
        if (mapContainer) {
          mapContainer.innerHTML = `
            <div class="flex items-center justify-center h-full bg-gray-100 text-gray-600">
              <div class="text-center">
                <p class="text-lg font-medium">Unable to load map</p>
                <p class="text-sm">Please check your internet connection and try again</p>
              </div>
            </div>
          `
        }
      }
    },
    
    loadGoogleMapsAPI() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve()
          return
        }
        
        // Check if script is already loading
        if (window.googleMapsLoading) {
          // Wait for the existing loading promise
          window.googleMapsLoading.then(resolve).catch(reject)
          return
        }
        
        // Create a new loading promise
        window.googleMapsLoading = new Promise((scriptResolve, scriptReject) => {
          const script = document.createElement('script')
          script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}&libraries=places`
          script.async = true
          script.defer = true
          
          script.onload = () => {
            // Add a small delay to ensure Google Maps is fully initialized
            setTimeout(() => {
              if (window.google && window.google.maps) {
                scriptResolve()
              } else {
                scriptReject(new Error('Google Maps API failed to initialize properly'))
              }
            }, 100)
          }
          
          script.onerror = () => {
            scriptReject(new Error('Google Maps API failed to load'))
          }
          
          document.head.appendChild(script)
        })
        
        // Use the shared loading promise
        window.googleMapsLoading.then(resolve).catch(reject)
      })
    },
    
    updateMarkers(shows) {
      if (!this.map || !shows) return
      
      // Clear all existing markers first
      this.markers.forEach(marker => {
        if (marker.infoWindow) {
          marker.infoWindow.close()
        }
        marker.setMap(null)
      })
      this.markers = []
      
      // Group shows by location
      const groupedShows = this.groupShowsByLocation(shows)
      
      // Add markers for each location group
      Object.values(groupedShows).forEach(locationGroup => {
        const position = {
          lat: locationGroup.latitude,
          lng: locationGroup.longitude
        }
        
        const marker = new window.google.maps.Marker({
          position: position,
          map: this.map,
          title: this.getMarkerTitle(locationGroup),
          icon: this.createCustomMarkerIcon(locationGroup.shows.length),
          opacity: 1.0
        })
        
        // Store the show IDs on the marker for future reference
        marker.showIds = locationGroup.shows.map(show => show.id)
        marker.locationGroup = locationGroup
        
        // Create info window with all shows at this location
        const infoWindow = new window.google.maps.InfoWindow({
          content: this.createInfoWindowContent(locationGroup.shows),
          disableAutoPan: false,
          maxWidth: 320,
          pixelOffset: new window.google.maps.Size(0, -10)
        })
        
        marker.addListener('click', () => {
          // Set flag to prevent bounds update during info window opening
          this.openingInfoWindow = true
          
          // Close any open info windows
          this.markers.forEach(m => {
            if (m.infoWindow) m.infoWindow.close()
          })
          
          // Open the new info window
          infoWindow.open(this.map, marker)
          
          // After info window opens, add event listeners
          window.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
            // Add click listeners to View Details buttons (for single events)
            const viewDetailsButtons = document.querySelectorAll('.view-details-btn')
            viewDetailsButtons.forEach(btn => {
              btn.addEventListener('click', (e) => {
                e.preventDefault()
                e.stopPropagation()
                // Get the event identifier from the first show (single event case)
                if (locationGroup.shows.length === 1) {
                  this.$router.push(`/${locationGroup.shows[0].event_identifier}`)
                }
              })
            })
            
            // Add click listeners to individual event items (for multiple events)
            const eventItems = document.querySelectorAll('.event-item')
            eventItems.forEach((item, index) => {
              item.addEventListener('click', (e) => {
                e.preventDefault()
                e.stopPropagation()
                if (locationGroup.shows[index]) {
                  this.$router.push(`/${locationGroup.shows[index].event_identifier}`)
                }
              })
            })
            
            // Add click listener to View All Events button
            const viewAllBtn = document.querySelector('.view-all-btn')
            if (viewAllBtn) {
              viewAllBtn.addEventListener('click', (e) => {
                e.preventDefault()
                e.stopPropagation()
                // Handle view all events - could emit event or navigate to filtered view
                this.$emit('view-all-events', locationGroup.shows)
              })
            }
            
            // Add click listener to custom close button
            const customCloseButton = document.querySelector('.close-button')
            if (customCloseButton) {
              customCloseButton.addEventListener('click', (e) => {
                e.preventDefault()
                e.stopPropagation()
                infoWindow.close()
              })
            }
          })
          
          // Reset the flag after a short delay
          setTimeout(() => {
            this.openingInfoWindow = false
          }, 300)
        })
        
        // Add listener for when info window is closed manually
        infoWindow.addListener('closeclick', () => {
          // Info window was closed by user clicking X
          // No special handling needed, just let it close
        })
        
        marker.infoWindow = infoWindow
        this.markers.push(marker)
      })
      
      // Update marker filtering after all markers are created
      this.updateRadiusFiltering()
    },
    
    groupShowsByLocation(shows) {
      const groups = {}
      
      shows.forEach(show => {
        if (show.location_details && show.location_details.geolocation) {
          const lat = show.location_details.geolocation.latitude
          const lng = show.location_details.geolocation.longitude
          
          // Create a location key with rounded coordinates to group nearby locations
          // Round to ~100m precision (approximately 0.001 degrees)
          const locationKey = `${Math.round(lat * 1000) / 1000},${Math.round(lng * 1000) / 1000}`
          
          if (!groups[locationKey]) {
            groups[locationKey] = {
              latitude: lat,
              longitude: lng,
              shows: [],
              locationKey: locationKey
            }
          }
          
          groups[locationKey].shows.push(show)
        }
      })
      
      return groups
    },
    
    getMarkerTitle(locationGroup) {
      if (locationGroup.shows.length === 1) {
        return locationGroup.shows[0].name
      } else {
        const locationName = this.getLocationNameFromShows(locationGroup.shows)
        return `${locationGroup.shows.length} events at ${locationName}`
      }
    },
    
    getLocationNameFromShows(shows) {
      if (shows.length === 0) return 'Unknown Location'
      
      const firstShow = shows[0]
      if (firstShow.location_details) {
        if (firstShow.location_details.location_name) {
          return firstShow.location_details.location_name
        }
        
        const city = firstShow.location_details.city
        const state = firstShow.location_details.state
        if (city && state) {
          return `${city}, ${state}`
        } else if (city) {
          return city
        } else if (state) {
          return state
        }
      }
      
      return 'Unknown Location'
    },
    
    createCustomMarkerIcon(eventCount) {
      if (eventCount === 1) {
        // Single event - use default marker
        return null
      }
      
      // Multiple events - create custom marker with count
      const size = eventCount > 9 ? 40 : 32
      const fontSize = eventCount > 9 ? '12px' : '14px'
      const fontWeight = 'bold'
      
      // Create SVG marker with count
      const svg = `
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
          <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 2}" fill="#dc2626" stroke="#ffffff" stroke-width="2"/>
          <text x="${size/2}" y="${size/2 + 1}" text-anchor="middle" dominant-baseline="central" 
                fill="white" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="${fontWeight}">
            ${eventCount > 99 ? '99+' : eventCount}
          </text>
        </svg>
      `
      
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        size: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size/2, size/2),
        scaledSize: new window.google.maps.Size(size, size)
      }
    },
    
    createInfoWindowContent(shows) {
      // Create a temporary Vue instance to render the component
      const InfoWindowComponent = Vue.extend(MapInfoWindow)
      const instance = new InfoWindowComponent({
        propsData: { shows: shows }, // Pass all shows for this location
        parent: this
      })
      
      // Mount the component and return the HTML
      instance.$mount()
      return instance.$el.outerHTML
    },
    
    showMapError() {
      const mapElement = document.getElementById('show-map')
      if (mapElement) {
        mapElement.innerHTML = `
          <div class="flex items-center justify-center h-full bg-gray-100 rounded-lg">
            <div class="text-center p-8">
              <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Map View</h3>
              <p class="text-gray-600 mb-2">Unable to load interactive map</p>
              <p class="text-sm text-gray-500 mb-4">Please check your internet connection</p>
              <div class="text-left bg-white p-4 rounded-lg shadow-sm max-w-md mx-auto">
                <h4 class="font-medium mb-2">Showing ${this.shows.length} upcoming shows:</h4>
                ${this.shows.slice(0, 5).map(show => `
                  <div class="mb-2 pb-2 border-b border-gray-200 last:border-b-0">
                    <p class="font-medium text-sm">${show.name}</p>
                    <p class="text-xs text-gray-600">${show.location_details ? `${show.location_details.city}, ${show.location_details.state}` : 'Location TBD'}</p>
                  </div>
                `).join('')}
                ${this.shows.length > 5 ? `<p class="text-xs text-gray-500 mt-2">... and ${this.shows.length - 5} more shows</p>` : ''}
              </div>
            </div>
          </div>
        `
      }
    },
    centerMapOnLocation(location) {
      if (this.map && location) {
        // Clear existing center marker
        if (this.centerMarker) {
          this.centerMarker.setMap(null)
          this.centerMarker = null
        }
        
        // Handle both old format (location.lat, location.lng) and new format (location.coordinates.lat, location.coordinates.lng)
        let lat, lng
        
        if (location.coordinates) {
          lat = location.coordinates.lat
          lng = location.coordinates.lng
        } else {
          lat = location.lat
          lng = location.lng
        }
        
        // Validate coordinates before using them
        if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
          const center = new window.google.maps.LatLng(lat, lng)
          this.map.setCenter(center)
          
          // Create center marker with distinctive style
          this.centerMarker = new window.google.maps.Marker({
            position: center,
            map: this.map,
            title: location.name || 'Selected Location',
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2
            },
            zIndex: 1000 // Ensure center marker appears above other markers
          })
          
          // Smart zoom based on location type
          this.setSmartZoom(location)
        } else {
          console.error('Invalid coordinates for location:', location)
        }
      }
    },
    centerMapOnUS() {
      if (this.map) {
        // Center on the geographic center of the continental US
        const center = new window.google.maps.LatLng(39.8283, -98.5795)
        this.map.setCenter(center)
        this.map.setZoom(4)
        
        // Clear center marker when location is cleared
        if (this.centerMarker) {
          this.centerMarker.setMap(null)
          this.centerMarker = null
        }
      }
    },
    updateMapBounds() {
      // Skip bounds update if:
      // 1. Map is not initialized
      // 2. Currently opening an info window
      if (!this.map || this.openingInfoWindow) return
      
      const bounds = this.map.getBounds()
      if (bounds) {
        const mapBounds = {
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng()
        }
        
        // Emit bounds change to parent instead of updating store directly
        this.$emit('bounds-changed', mapBounds)
      }
    },
    
    resetUserInteractionFlag() {
      // Reset the flag after a short delay to allow for natural map interactions
      setTimeout(() => {
        this.userInteracting = false
      }, 500) // Reduced timeout for more responsive bounds updates
    },
    
    setSmartZoom(location) {
      if (!this.map || !location) return
      
      // Check if this is a region selection
      const isRegionSelection = location.type === 'region'
      
      // Check if this is a state selection with multiple criteria
      const isStateSelection = location.type === 'state' || 
        (location.state_info && location.state_info.code && !location.city) ||
        (location.types && location.types.includes('administrative_area_level_1')) ||
        (location.place_type && location.place_type === 'state')
      
      if (isRegionSelection) {
        // For regions, use a very wide zoom (2 levels further out than states)
        this.map.setZoom(5)
      } else if (isStateSelection) {
        // Get state code from various possible sources
        let stateCode = null
        if (location.state_info && location.state_info.code) {
          stateCode = location.state_info.code.toUpperCase()
        } else if (location.administrative_area_level_1) {
          // Try to extract state code from administrative area
          const stateAbbr = this.getStateCodeFromName(location.administrative_area_level_1)
          if (stateAbbr) {
            stateCode = stateAbbr
          }
        } else if (location.name) {
          // Try to extract from location name
          const stateAbbr = this.getStateCodeFromName(location.name)
          if (stateAbbr) {
            stateCode = stateAbbr
          }
        }
        
        if (stateCode && this.stateBounds[stateCode]) {
          // Use predefined state bounds for perfect state fit
          const bounds = this.stateBounds[stateCode]
          const googleBounds = new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(bounds.south, bounds.west),
            new window.google.maps.LatLng(bounds.north, bounds.east)
          )
          
          // Fit map to state bounds with padding
          this.map.fitBounds(googleBounds, {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50
          })
          
        } else {
          // Fallback to default state zoom level (reduced from 6 to 4)
          this.map.setZoom(4)
        }
      } else {
        // For cities, zip codes, and specific locations, use standard zoom (reduced from 10 to 8)
        this.map.setZoom(8)
      }
    },
    
    getStateCodeFromName(stateName) {
      const stateNameToCode = {
        'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
        'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
        'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
        'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
        'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
        'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
        'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
        'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
        'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
        'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
      }
      
      return stateNameToCode[stateName] || null
    },

    // Add method to create custom Google Maps control
    createZoomControl() {
      const controlDiv = document.createElement('div')
      controlDiv.style.margin = '10px 12px 10px 10px' // Increased top margin to position below fullscreen button
      
      // Create zoom in button
      const zoomInDiv = document.createElement('div')
      zoomInDiv.style.backgroundColor = '#fff'
      zoomInDiv.style.border = '1px solid #ccc'
      zoomInDiv.style.borderRadius = '18px 18px 0 0'
      zoomInDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)'
      zoomInDiv.style.cursor = 'pointer'
      zoomInDiv.style.textAlign = 'center'
      zoomInDiv.style.width = '40px'
      zoomInDiv.style.height = '40px'
      zoomInDiv.style.lineHeight = '40px'
      zoomInDiv.style.fontSize = '18px'
      zoomInDiv.style.fontWeight = 'bold'
      zoomInDiv.style.color = '#484848'
      zoomInDiv.style.userSelect = 'none'
      zoomInDiv.innerHTML = '+'
      zoomInDiv.title = 'Zoom in'
      
      // Create zoom out button
      const zoomOutDiv = document.createElement('div')
      zoomOutDiv.style.backgroundColor = '#fff'
      zoomOutDiv.style.border = '1px solid #ccc'
      zoomOutDiv.style.borderTop = 'none'
      zoomOutDiv.style.borderRadius = '0 0 18px 18px'
      zoomOutDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)'
      zoomOutDiv.style.cursor = 'pointer'
      zoomOutDiv.style.textAlign = 'center'
      zoomOutDiv.style.width = '40px'
      zoomOutDiv.style.height = '40px'
      zoomOutDiv.style.lineHeight = '40px'
      zoomOutDiv.style.fontSize = '18px'
      zoomOutDiv.style.fontWeight = 'bold'
      zoomOutDiv.style.color = '#484848'
      zoomOutDiv.style.userSelect = 'none'
      zoomOutDiv.innerHTML = '−'
      zoomOutDiv.title = 'Zoom out'
      
      // Add hover effects
      const addHoverEffect = (element) => {
        element.addEventListener('mouseenter', () => {
          element.style.backgroundColor = '#f7f7f7'
        })
        element.addEventListener('mouseleave', () => {
          element.style.backgroundColor = '#fff'
        })
      }
      
      addHoverEffect(zoomInDiv)
      addHoverEffect(zoomOutDiv)
      
      // Add click events
      zoomInDiv.addEventListener('click', () => {
        if (this.map) {
          this.map.setZoom(this.map.getZoom() + 1)
        }
      })
      
      zoomOutDiv.addEventListener('click', () => {
        if (this.map) {
          this.map.setZoom(this.map.getZoom() - 1)
        }
      })
      
      // Append buttons to control div
      controlDiv.appendChild(zoomInDiv)
      controlDiv.appendChild(zoomOutDiv)
      
      return controlDiv
    },

    // Add method to create boundary control
    createBoundaryControl() {
      const controlDiv = document.createElement('div')
      controlDiv.style.margin = '10px'
      controlDiv.style.display = 'none' // Initially hidden
      
      // Create the boundary info container
      const boundaryDiv = document.createElement('div')
      boundaryDiv.style.backgroundColor = '#fff'
      boundaryDiv.style.border = '1px solid #d1d5db'
      boundaryDiv.style.borderRadius = '8px'
      boundaryDiv.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      boundaryDiv.style.padding = '8px 16px'
      boundaryDiv.style.display = 'flex'
      boundaryDiv.style.alignItems = 'center'
      boundaryDiv.style.gap = '12px'
      boundaryDiv.style.fontSize = '14px'
      boundaryDiv.style.fontFamily = 'system-ui, -apple-system, sans-serif'
      
      // Create the blue circle indicator
      const circleDiv = document.createElement('div')
      circleDiv.style.width = '12px'
      circleDiv.style.height = '12px'
      circleDiv.style.borderRadius = '50%'
      circleDiv.style.backgroundColor = '#3b82f6'
      circleDiv.style.border = '2px solid #fff'
      circleDiv.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
      circleDiv.style.flexShrink = '0'
      
      // Create the text span
      const textSpan = document.createElement('span')
      textSpan.style.fontWeight = '500'
      textSpan.style.color = '#374151'
      
      // Create the remove button
      const removeButton = document.createElement('button')
      removeButton.style.display = 'flex'
      removeButton.style.alignItems = 'center'
      removeButton.style.gap = '4px'
      removeButton.style.padding = '4px 12px'
      removeButton.style.fontSize = '14px'
      removeButton.style.color = '#2563eb'
      removeButton.style.backgroundColor = 'transparent'
      removeButton.style.border = 'none'
      removeButton.style.borderRadius = '6px'
      removeButton.style.cursor = 'pointer'
      removeButton.style.transition = 'all 0.2s'
      removeButton.innerHTML = `
        <svg style="width: 16px; height: 16px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        Remove Boundary
      `
      
      // Add hover effect to remove button
      removeButton.addEventListener('mouseenter', () => {
        removeButton.style.color = '#1d4ed8'
        removeButton.style.backgroundColor = '#eff6ff'
      })
      removeButton.addEventListener('mouseleave', () => {
        removeButton.style.color = '#2563eb'
        removeButton.style.backgroundColor = 'transparent'
      })
      
      // Add click event to remove button
      removeButton.addEventListener('click', () => {
        this.removeBoundary()
      })
      
      // Assemble the control
      boundaryDiv.appendChild(circleDiv)
      boundaryDiv.appendChild(textSpan)
      boundaryDiv.appendChild(removeButton)
      controlDiv.appendChild(boundaryDiv)
      
      // Store references for updates
      controlDiv._textSpan = textSpan
      controlDiv._boundaryDiv = boundaryDiv
      
      return controlDiv
    },

    // Update boundary control visibility and text
    updateBoundaryControl() {
      if (!this.boundaryControl) return
      
      const shouldShow = this.radiusCircle && this.selectedLocation && this.selectedRadius
      
      if (shouldShow) {
        // Update text
        const radiusText = this.selectedRadius === 1 ? '1 mile' : `${this.selectedRadius} miles`
        const locationName = this.selectedLocation.name || 'Selected Location'
        this.boundaryControl._textSpan.textContent = `${radiusText} from ${locationName}`
        
        // Show the control
        this.boundaryControl.style.display = 'block'
      } else {
        // Hide the control
        this.boundaryControl.style.display = 'none'
      }
    },
    
    // Add radius filtering methods
    updateRadiusFiltering() {
      if (!this.map || !this.selectedLocation || !this.selectedRadius) return
      
      // Clear existing radius filtering visuals
      this.clearRadiusFiltering()
      
      // Get center coordinates
      let centerLat, centerLng
      if (this.selectedLocation.coordinates) {
        centerLat = this.selectedLocation.coordinates.lat
        centerLng = this.selectedLocation.coordinates.lng
      } else {
        centerLat = this.selectedLocation.lat
        centerLng = this.selectedLocation.lng
      }
      
      const center = new window.google.maps.LatLng(centerLat, centerLng)
      
      // Create radius circle
      this.radiusCircle = new window.google.maps.Circle({
        strokeColor: '#4285F4',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#4285F4',
        fillOpacity: 0.15,
        map: this.map,
        center: center,
        radius: this.selectedRadius * 1609.34 // Convert miles to meters
      })
      
      // Adjust zoom to show the full radius
      this.adjustZoomForRadius(center, this.selectedRadius)
      
      // Filter shows and create connection lines
      this.filterShowsByRadius(center, this.selectedRadius)
      
      // Update boundary control
      this.updateBoundaryControl()
    },

    // Add method to calculate and set appropriate zoom level for radius
    adjustZoomForRadius(center, radiusMiles) {
      if (!this.map || !center || !radiusMiles) return
      
      // Create bounds that include the entire radius circle
      const bounds = new window.google.maps.LatLngBounds()
      
      // Calculate the approximate degrees per mile at this latitude
      const lat = center.lat()
      const milesPerDegreeLat = 69 // Approximately 69 miles per degree of latitude
      const milesPerDegreeLng = Math.cos(lat * Math.PI / 180) * 69 // Longitude varies by latitude
      
      // Calculate the bounding box for the radius
      const latOffset = radiusMiles / milesPerDegreeLat
      const lngOffset = radiusMiles / milesPerDegreeLng
      
      // Extend bounds to include the radius circle
      bounds.extend(new window.google.maps.LatLng(lat + latOffset, center.lng() + lngOffset))
      bounds.extend(new window.google.maps.LatLng(lat - latOffset, center.lng() - lngOffset))
      bounds.extend(new window.google.maps.LatLng(lat + latOffset, center.lng() - lngOffset))
      bounds.extend(new window.google.maps.LatLng(lat - latOffset, center.lng() + lngOffset))
      
      // Fit the map to these bounds with some padding
      this.map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 80, // Extra padding at bottom for boundary control
        left: 50
      })
    },
    
    filterShowsByRadius(center, radiusMiles) {
      if (!this.markers || this.markers.length === 0) return
      
      this.filteredMarkers = []
      
      this.markers.forEach(marker => {
        const distance = this.calculateDistance(
          center.lat(),
          center.lng(),
          marker.getPosition().lat(),
          marker.getPosition().lng()
        )
        
        if (distance <= radiusMiles) {
          // Location group is within radius - full opacity
          marker.setOpacity(1.0)
          this.filteredMarkers.push(marker)
        } else {
          // Location group is outside radius - semi-transparent
          marker.setOpacity(0.3)
        }
      })
    },
    
    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 3959 // Radius of the Earth in miles
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLng = (lng2 - lng1) * Math.PI / 180
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c // Distance in miles
    },
    
    clearRadiusFiltering() {
      // Clear radius circle
      if (this.radiusCircle) {
        this.radiusCircle.setMap(null)
        this.radiusCircle = null
      }
      
      // Reset all markers to full opacity
      this.markers.forEach(marker => {
        marker.setOpacity(1.0)
      })
      
      this.filteredMarkers = []
      
      // Update boundary control
      this.updateBoundaryControl()
    },

    removeBoundary() {
      // Set flag to prevent map repositioning
      this.clearingFromBoundaryControl = true
      
      this.clearRadiusFiltering()
      
      // Emit event to parent to clear location filter (including radius)
      this.$emit('clear-location')
      
      // Reset flag after a short delay
      setTimeout(() => {
        this.clearingFromBoundaryControl = false
      }, 100)
    }
  },
  
  beforeDestroy() {
    // Clean up markers
    if (this.markers) {
      this.markers.forEach(marker => marker.setMap(null))
    }
    
    // Clean up center marker
    if (this.centerMarker) {
      this.centerMarker.setMap(null)
    }
  }
}
</script>

<style scoped>
.show-map-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

#show-map {
  height: 100%;
  min-height: 400px;
  position: relative;
}

/* Ensure overlays appear above Google Maps */
.show-map-container > .absolute {
  position: absolute !important;
  z-index: 9999 !important;
}
</style> 